import * as React from "react"

import {
  AFAXYS_PRIVACY_POLICY_HREF,
  AFAXYS_TERMS_OF_USE_HREF,
} from "../../data/legal"

const BRAND_IMAGE =
  "/figma/shared/brand.png"
const FOOTER_WATERMARK =
  "/figma/shared/footer-watermark-consumer.png"
const SOCIAL_INSTAGRAM =
  "/figma/shared/social-instagram.svg"
const SOCIAL_TIKTOK =
  "/figma/shared/social-tiktok.svg"
const SOCIAL_YOUTUBE =
  "/figma/shared/social-youtube.svg"

const cookiePolicyHref = AFAXYS_PRIVACY_POLICY_HREF
const adverseReactionHref = "https://www.fda.gov/medwatch"
const footerTagline =
  "#1 provider of oral and emergency contraceptives dispensed in US clinics*"
const footerFootnote = "*Based on IQVIA data"
const footerCopyright =
  "\u00a9 2026 Afaxys Pharma, LLC. Charleston, SC 29413. All Rights Reserved. APH-811-0126 June 2026"
const footerLogoHref = "https://afaxyspharma.com/patients"
const montserratStyle: React.CSSProperties = {
  fontFamily: '"Montserrat", var(--font-sans)',
}
const oxygenStyle: React.CSSProperties = {
  fontFamily: '"Oxygen", var(--font-sans)',
}

/*
 * Figma includes consumer-footer endpoints that are not publicly available yet.
 * Use the closest live Afaxys destinations until final consumer legal URLs are provided.
 */
const footerLinks = [
  {
    href: "https://www.afaxys.com/afaxys-websites-accessibility-statement/",
    label: "Accessibility Statement",
  },
  {
    href: "https://www.afaxys.com/terms-of-use",
    label: "Terms of Use",
  },
  {
    href: "https://www.afaxys.com/privacy-policy",
    label: "Privacy Policy",
  },
  {
    href: "https://www.afaxys.com/health-data-privacy-policy",
    label: "Health Data Policy",
  },
  {
    href: "https://www.afaxys.com/product-purchase-agreement-terms-and-conditions",
    label: "EContra Terms of Purchase",
  },
] as const

const socialLinks = [
  {
    alt: "Instagram",
    href: "https://www.instagram.com/econtraonestep",
    src: SOCIAL_INSTAGRAM,
  },
  {
    alt: "TikTok",
    href: "https://www.tiktok.com/@econtraonestep",
    src: SOCIAL_TIKTOK,
  },
  {
    alt: "YouTube",
    href: "https://www.youtube.com/@EContraOne-Step",
    src: SOCIAL_YOUTUBE,
  },
] as const

type ConsumerFooterProps = {
  className?: string
}

type SocialIconProps = {
  alt: string
  href: string
  sizeClassName: string
  src: string
}

function SocialIcon({
  alt,
  href,
  sizeClassName,
  src,
}: SocialIconProps) {
  return (
    <a
      aria-label={alt}
      className={[
        "block shrink-0",
        sizeClassName,
      ]
        .filter(Boolean)
        .join(" ")}
      href={href}
      rel="noreferrer"
      target="_blank"
    >
      <img
        alt=""
        aria-hidden="true"
        className="block size-full"
        src={src}
      />
    </a>
  )
}

function FooterAdverseReactionCopy() {
  return (
    <p className="font-sans text-[14px] leading-[21px] tracking-[-0.154px] text-ink-950">
      To report SUSPECTED ADVERSE REACTIONS, call 1-855-888-2467 or the FDA at
      1-800-FDA-1088 or{" "}
      <a
        className="underline"
        href={adverseReactionHref}
        rel="noreferrer"
        target="_blank"
      >
        www.fda.gov/medwatch
      </a>
    </p>
  )
}

function FooterCookieCopy() {
  return (
    <p className="font-sans text-[14px] leading-[21px] tracking-[-0.154px] text-ink-950">
      Cookie notice: This site uses cookies to personalize content, save your
      preferences, and track the site's performance. Learn more, including how
      to disable certain cookies on our{" "}
      <a
        className="font-bold underline"
        href={cookiePolicyHref}
        rel="noreferrer"
        target="_blank"
      >
        Privacy Policy
      </a>
      .
    </p>
  )
}

function DesktopFooter() {
  return (
    <div className="relative z-[1] mx-auto hidden w-full max-w-[1512px] px-5 lg:flex lg:flex-col lg:pb-[82px] lg:pt-10 xl:px-20">
      <div className="mx-auto flex w-full max-w-[1374px] flex-col gap-7">
        <div className="relative min-h-[148px] w-full">
          <div className="w-full">
            <div className="w-full whitespace-nowrap font-sans text-[13px] font-bold leading-[18px] tracking-[-0.154px] text-ink-950 xl:text-[14px] xl:leading-[19px]">
              {footerLinks.map((link, index) => (
                <React.Fragment key={link.label}>
                  <a
                    className="shrink-0 underline"
                    href={link.href}
                    rel="noreferrer"
                    target="_blank"
                  >
                    {link.label}
                  </a>
                  {index < footerLinks.length - 1 ? (
                    <span
                      aria-hidden="true"
                      className="px-3 text-ink-950"
                    >
                      |
                    </span>
                  ) : null}
                </React.Fragment>
              ))}
            </div>

            <div className="mt-[23px] w-full max-w-[1372px] lg:max-w-[calc(100%-244px)] 2xl:max-w-[1372px]">
              <FooterAdverseReactionCopy />
            </div>

            <div className="mt-[20px] w-[815px] max-w-full lg:max-w-[calc(100%-300px)] xl:max-w-[815px]">
              <FooterCookieCopy />
            </div>
          </div>

          <div className="absolute right-0 top-0 flex w-[220px] items-center justify-between">
            {socialLinks.map(link => (
              <SocialIcon
                key={link.alt}
                alt={link.alt}
                href={link.href}
                sizeClassName="h-[60px] w-[60px]"
                src={link.src}
              />
            ))}
          </div>
        </div>

        <div className="-mt-[42px] flex min-h-[118px] w-full items-end justify-between gap-6 xl:gap-10">
          <p className="font-sans text-[13px] leading-[18px] tracking-[-0.154px] text-ink-950 xl:text-[14px] xl:leading-[21px]">
            {footerCopyright}
          </p>

          <div className="-mt-[30px] flex w-[510px] shrink-0 flex-col items-end gap-[10px]">
            <a
              aria-label="Afaxys Pharma"
              href={footerLogoHref}
              rel="noreferrer"
              target="_blank"
            >
              <img
                  alt="Afaxys Pharma logo"
                className="h-[113px] w-auto"
                src={BRAND_IMAGE}
              />
            </a>
            <p
              className="w-[387px] text-right text-[16px] leading-[21px] text-ink-950"
              style={montserratStyle}
            >
              {footerTagline}
            </p>
            <p
              className="w-[251px] text-right text-[12px] leading-[15px] text-ink-950"
              style={oxygenStyle}
            >
              {footerFootnote}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/* TODO: Confirm final footer link destinations and social URLs from production requirements before wiring navigation. */
/* TODO: Preserve the shared desktop/mobile footer layouts from AFX-Footer-dt and AFX-Footer-m without pushing this copy back into route pages. */
export const ConsumerFooter: React.FC<ConsumerFooterProps> = ({ className }) => {
  const classes = [
    "relative w-full overflow-hidden border-t border-[#00341c] bg-[#c2ecff] text-ink-950",
    className,
  ]
    .filter(Boolean)
    .join(" ")

  return (
    <footer className={classes}>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 hidden lg:block"
      >
        <div className="absolute inset-0 overflow-hidden opacity-30">
          <img
            alt=""
            className="absolute left-[7.37%] top-[4.33%] h-[111.61%] w-auto max-w-none"
            src={FOOTER_WATERMARK}
          />
        </div>
      </div>

      <DesktopFooter />

      <div className="relative z-[1] flex flex-col items-center gap-[15px] px-6 pb-5 pt-[35px] text-center lg:hidden">
        <div className="flex items-center gap-6">
          {socialLinks.map(link => (
            <SocialIcon
              key={link.alt}
              alt={link.alt}
              href={link.href}
              sizeClassName="h-11 w-11"
              src={link.src}
            />
          ))}
        </div>

        <div className="flex h-[98px] w-full items-center justify-center pb-[19px] pt-[10px]">
          <a
            aria-label="Afaxys Pharma"
            href={footerLogoHref}
            rel="noreferrer"
            target="_blank"
          >
        <img alt="Afaxys Pharma logo" className="h-[76px] w-auto" src={BRAND_IMAGE} />
          </a>
        </div>

        <p
          className="max-w-[292px] text-[14px] leading-[21px] text-ink-950"
          style={montserratStyle}
        >
          {footerTagline}
        </p>

        <div className="flex flex-col items-center gap-[15px] py-[15px] font-sans text-[16px] font-bold leading-[19.2px] tracking-[-0.176px] text-ink-950">
          {footerLinks.map(link => (
            <a
              key={link.label}
              className="underline"
              href={link.href}
              rel="noreferrer"
              target="_blank"
            >
              {link.label}
            </a>
          ))}
        </div>

        <div className="w-full">
          <FooterAdverseReactionCopy />
          <div className="mt-5">
            <FooterCookieCopy />
          </div>
        </div>

        <p
          className="text-[12px] leading-[15px] text-ink-950"
          style={oxygenStyle}
        >
          {footerFootnote}
        </p>

        <p className="w-full font-sans text-[14px] leading-[21px] tracking-[-0.154px] text-ink-950">
          {footerCopyright}
        </p>
      </div>
    </footer>
  )
}
